import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Loader2, Download, X, FileText, AlertTriangle, Printer,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * PDFPreviewModal - Canvas-based PDF preview with proper lifecycle management
 * Fixed: Prevents DOM operations after unmount, proper cleanup of async tasks
 */
export function PDFPreviewModal({
  isOpen,
  onClose,
  pdfBlob,
  isLoading,
  error,
  title = "Anteprima Fattura",
  // Office-spec filename used by the download button. When omitted falls
  // back to "fattura.pdf" (legacy behaviour). The caller is encouraged
  // to compose it via `buildInvoicePdfFilename` in `utils/helpers`.
  filename,
}) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [rendering, setRendering] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  
  // Refs for lifecycle management
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const blobUrlRef = useRef(null);
  const pdfDocRef = useRef(null);
  const isMountedRef = useRef(true);
  const loadingTaskRef = useRef(null);

  // Safe state setters - only update if mounted
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  // Cleanup blob URL
  const cleanupBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
      } catch (e) { /* ignore */ }
      blobUrlRef.current = null;
    }
  }, []);

  // Cleanup render task
  const cleanupRenderTask = useCallback(() => {
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) { /* ignore */ }
      renderTaskRef.current = null;
    }
  }, []);

  // Cleanup loading task
  const cleanupLoadingTask = useCallback(() => {
    if (loadingTaskRef.current) {
      try {
        loadingTaskRef.current.destroy();
      } catch (e) { /* ignore */ }
      loadingTaskRef.current = null;
    }
  }, []);

  // Cleanup PDF document
  const cleanupPdfDoc = useCallback(() => {
    if (pdfDocRef.current) {
      try {
        pdfDocRef.current.destroy();
      } catch (e) { /* ignore */ }
      pdfDocRef.current = null;
    }
  }, []);

  // Full cleanup function
  const cleanup = useCallback(() => {
    cleanupRenderTask();
    cleanupLoadingTask();
    cleanupPdfDoc();
    cleanupBlobUrl();
  }, [cleanupRenderTask, cleanupLoadingTask, cleanupPdfDoc, cleanupBlobUrl]);

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Load PDF document when blob changes
  useEffect(() => {
    // When there's no blob to render, reset display state. We do NOT
    // include `!isOpen` here on purpose: the parent already defers the
    // `pdfBlob=null` reset past the close animation (see
    // `useInvoicesDeadlines.closePreviewModal`), so as long as the blob
    // is still present the modal can keep rendering safely while the
    // Radix portal animates out. Triggering pdfjs cleanup mid-animation
    // races the portal teardown → React-19 removeChild crash.
    if (!pdfBlob) {
      safeSetState(setPdfDoc, null);
      safeSetState(setNumPages, 0);
      safeSetState(setPageNum, 1);
      safeSetState(setPdfError, null);
      cleanup();
      return;
    }
    if (!isOpen) {
      // Modal hidden but blob still present (transient state during
      // close animation) — do nothing; the mount-tracking effect will
      // clean up on real unmount.
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      try {
        // Cleanup previous
        cleanup();

        // Check if still mounted and open
        if (!isMountedRef.current || cancelled) return;

        // Create blob URL for download functionality
        const url = URL.createObjectURL(pdfBlob);
        blobUrlRef.current = url;
        
        if (!isMountedRef.current || cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        
        safeSetState(setBlobUrl, url);

        // Convert blob to ArrayBuffer
        const arrayBuffer = await pdfBlob.arrayBuffer();
        
        if (!isMountedRef.current || cancelled) return;

        // Load PDF document
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        loadingTaskRef.current = loadingTask;
        
        const pdf = await loadingTask.promise;
        
        if (!isMountedRef.current || cancelled) {
          pdf.destroy();
          return;
        }

        pdfDocRef.current = pdf;
        safeSetState(setPdfDoc, pdf);
        safeSetState(setNumPages, pdf.numPages);
        safeSetState(setPageNum, 1);
        safeSetState(setPdfError, null);
      } catch (err) {
        if (!isMountedRef.current || cancelled) return;
        if (err.name === 'AbortError' || err.message?.includes('destroy')) return;
        
        console.error("PDF load error:", err);
        safeSetState(setPdfError, "Impossibile caricare il PDF: " + (err.message || "errore sconosciuto"));
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      cleanupLoadingTask();
    };
  }, [pdfBlob, isOpen, cleanup, cleanupLoadingTask, safeSetState]);

  // Render current page to canvas
  useEffect(() => {
    // Guard: check all required conditions
    if (!pdfDoc || !isOpen) return;
    
    // Guard: verify canvas exists in DOM
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentNode) return;

    let cancelled = false;

    const renderPage = async () => {
      // Cancel any ongoing render
      cleanupRenderTask();
      
      if (!isMountedRef.current || cancelled) return;

      safeSetState(setRendering, true);

      try {
        const page = await pdfDoc.getPage(pageNum);
        
        if (!isMountedRef.current || cancelled) return;
        
        // Re-verify canvas still exists
        const currentCanvas = canvasRef.current;
        if (!currentCanvas || !currentCanvas.parentNode) {
          safeSetState(setRendering, false);
          return;
        }

        const viewport = page.getViewport({ scale });
        const context = currentCanvas.getContext("2d");
        
        if (!context) {
          safeSetState(setRendering, false);
          return;
        }

        currentCanvas.height = viewport.height;
        currentCanvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        
        await renderTask.promise;
        
        if (isMountedRef.current && !cancelled) {
          safeSetState(setRendering, false);
        }
      } catch (err) {
        if (!isMountedRef.current || cancelled) return;
        if (err.name === "RenderingCancelledException") return;
        
        console.error("Page render error:", err);
        safeSetState(setRendering, false);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      cleanupRenderTask();
    };
  }, [pdfDoc, pageNum, scale, isOpen, cleanupRenderTask, safeSetState]);

  // Reset on modal close.
  //
  // React 19 + Radix Dialog: when `isOpen` flips to false, the parent
  // portal starts a ~200ms exit animation while the children stay
  // mounted with `data-state="closed"`. We deliberately AVOID running
  // `cleanup()` (which destroys the pdfjs document, revokes the blob
  // URL, and cancels in-flight tasks) on the same render: that
  // synchronously toggles the canvas display style and races Radix's
  // portal teardown, surfacing as
  //   "Failed to execute 'removeChild' on 'Node'".
  //
  // Instead we let the mount-tracking effect (returned cleanup of
  // useEffect at line 101-107) handle teardown when the modal truly
  // unmounts. State-only resets here are cheap and react-safe.
  useEffect(() => {
    if (!isOpen) {
      safeSetState(setRendering, false);
    }
  }, [isOpen, safeSetState]);

  // Navigation
  const goToPrevPage = useCallback(() => {
    setPageNum(prev => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNum(prev => Math.min(prev + 1, numPages));
  }, [numPages]);

  // Zoom
  const zoomIn = useCallback(() => setScale(prev => Math.min(prev + 0.25, 3)), []);
  const zoomOut = useCallback(() => setScale(prev => Math.max(prev - 0.25, 0.5)), []);

  // Download
  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "fattura.pdf";
    link.click();
  }, [blobUrl, filename]);

  // Print — apre il PDF in una nuova tab E imposta `document.title`
  // al filename canonico, così quando l'utente sceglie
  // "Stampa → Salva come PDF" dalla nuova tab, il browser propone
  // il nome corretto (es. "Fattura 156P del 19052026 DE CURZIO ALESSANDRA.pdf")
  // invece dell'UUID del blob URL.
  //
  // Fallback: se il browser blocca window.open o l'iframe load,
  // ripieghiamo sul vecchio `window.open(blobUrl)` (peggior caso:
  // titolo UUID ma stampa funziona comunque).
  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    // Strip .pdf trailing ext when settando il document.title — i browser
    // ri-aggiungono l'estensione automaticamente nel Save dialog.
    const titleNoExt = (filename || "fattura.pdf").replace(/\.pdf$/i, "");
    const w = window.open("", "_blank");
    if (!w) {
      // popup bloccato → fallback minimo (vecchio comportamento)
      window.open(blobUrl, "_blank");
      return;
    }
    const html = `<!doctype html><html><head>
      <meta charset="utf-8" />
      <title>${titleNoExt.replace(/[<>&]/g, c => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[c]))}</title>
      <style>
        html,body{margin:0;padding:0;height:100%;background:#525659}
        iframe{border:0;width:100%;height:100%}
      </style>
    </head><body>
      <iframe src="${blobUrl}" title="${titleNoExt}"></iframe>
    </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  }, [blobUrl, filename]);

  // Determine display states
  const showLoading = isLoading || (pdfBlob && !pdfDoc && !pdfError);
  const showError = !isLoading && (error || pdfError);
  const showPdf = pdfDoc && !error && !pdfError;

  // React 19 + Radix Dialog + PDF.js hardening
  // -----------------------------------------------------------------
  // Repeated open/close cycles on real Windows Chrome/Edge (not just
  // headless automation) were still surfacing
  //   "Failed to execute 'removeChild' on 'Node': The node to be
  //    removed is not a child of this node."
  // The previous attempts deferred state cleanup with setTimeout to
  // wait out the Radix close animation, but on slower machines the
  // timer can fire on either side of the actual portal teardown,
  // which keeps the race alive.
  //
  // The only race-free guarantee is to FULLY UNMOUNT the Dialog tree
  // synchronously when the parent flips `isOpen` to false. By
  // returning `null` early, React tears down the entire subtree
  // (Dialog portal + canvas + pdf.js refs) in a single commit, and
  // the mount-tracking useEffect cleanup at line 101-107 runs
  // immediately. No leftover canvas, no orphan Radix portal nodes,
  // no race window for PDF.js worker callbacks to touch detached
  // DOM. We deliberately give up the ~200ms close fade animation in
  // exchange for crash-free behaviour — confirmed acceptable trade
  // by the user during local validation.
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0" data-testid="pdf-preview-modal">
        <DialogHeader className="px-4 py-3 border-b bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-blue-600" />
              <div className="flex flex-col leading-tight">
                <span>{title}</span>
                {filename && (
                  <span
                    className="text-[11px] font-normal text-slate-500"
                    title={filename}
                    data-testid="pdf-preview-filename"
                  >
                    {filename}
                  </span>
                )}
              </div>
            </DialogTitle>
            <div className="flex items-center gap-1">
              {/* Zoom controls */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={zoomOut} 
                disabled={scale <= 0.5 || !showPdf} 
                className="h-8 w-8 p-0" 
                title="Riduci"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs text-slate-600 w-12 text-center">{Math.round(scale * 100)}%</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={zoomIn} 
                disabled={scale >= 3 || !showPdf} 
                className="h-8 w-8 p-0" 
                title="Ingrandisci"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              
              <div className="w-px h-6 bg-slate-200 mx-2" />
              
              {/* Actions */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrint}
                disabled={!blobUrl || isLoading}
                className="h-8 px-2 text-xs gap-1"
                title="Stampa"
                data-testid="print-pdf-btn"
              >
                <Printer className="h-3.5 w-3.5" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleDownload} 
                disabled={!blobUrl || isLoading} 
                className="h-8 px-2" 
                title="Scarica"
                data-testid="download-pdf-btn"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClose} 
                className="h-8 w-8 p-0"
                data-testid="close-preview-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        {/* PDF Viewer - Canvas-based rendering with stable container */}
        <div 
          className="flex-1 bg-slate-300 overflow-auto flex items-start justify-center p-4" 
          data-testid="pdf-viewer-container"
        >
          {/* Loading state */}
          {showLoading && (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              <p className="text-slate-700 font-medium">Caricamento PDF...</p>
            </div>
          )}
          
          {/* Error state */}
          {showError && (
            <div className="flex flex-col items-center justify-center gap-4 p-8">
              <AlertTriangle className="h-12 w-12 text-red-500" />
              <p className="text-red-600 font-medium text-center">{error || pdfError}</p>
              {blobUrl && (
                <Button onClick={handleDownload} variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Scarica PDF
                </Button>
              )}
            </div>
          )}
          
          {/* PDF Canvas - Always render canvas container for stability */}
          <div 
            className="relative"
            style={{ display: showPdf ? 'block' : 'none' }}
          >
            {rendering && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            )}
            <canvas 
              ref={canvasRef} 
              className="shadow-lg bg-white"
              data-testid="pdf-canvas"
            />
          </div>
        </div>
        
        {/* Footer with pagination */}
        <DialogFooter className="px-4 py-2 border-t bg-slate-50 flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-slate-500">
              Anteprima fattura
            </p>
            
            {numPages > 1 && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToPrevPage} 
                  disabled={pageNum <= 1} 
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-600 min-w-[80px] text-center">
                  Pagina {pageNum} di {numPages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToNextPage} 
                  disabled={pageNum >= numPages} 
                  className="h-7 w-7 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {numPages === 1 && <span className="text-xs text-slate-500">1 pagina</span>}
            
            <Button variant="outline" size="sm" onClick={onClose} data-testid="close-preview-footer-btn">
              Chiudi
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
